import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, TrophyIcon, VideoCameraIcon, RectangleStackIcon, WalletIcon, CurrencyDollarIcon, PlusIcon, Cog6ToothIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { ADMIN_ADDRESSES } from './ProtectedAdminRoute';
import SidebarWallet from './SidebarWallet';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import LoginOptions from './LoginOptions';

const Sidebar = () => {
  const { user } = useAuth();
  const { isOpen, toggleSidebar } = useSidebar();
  const location = useLocation();

  const baseMenuItems = [
    {
      name: 'Home',
      icon: HomeIcon,
      path: '/',
      internal: true
    },
    {
      name: 'BitSport Live',
      icon: VideoCameraIcon,
      path: '/bitsport-live',
      internal: true
    },
    {
      name: 'Tournaments',
      icon: RectangleStackIcon,
      path: '/tournaments',
      internal: true
    },
    {
      name: 'BitPool',
      icon: TrophyIcon,
      path: '/bitpool',
      internal: true,
      highlight: true
    }
  ];

  // Add admin-only menu items
  const menuItems = [...baseMenuItems];
  if (ADMIN_ADDRESSES.includes(user?.address?.toLowerCase()) || user?.isAdmin) {
    menuItems.splice(4, 0, {
      name: 'Admin Panel',
      icon: PlusIcon,
      path: '/admin',
      internal: true,
      adminOnly: true,
      highlight: true
    }, {
      name: '+ Create Quest',
      icon: PlusIcon,
      path: '/admin/create-quest',
      internal: true,
      adminOnly: true,
      highlight: true
    });
  }

  // Add quests as a menu item
  if (!menuItems.some(item => item.path === '/quests')) {
    menuItems.push({
      name: 'AI Agent Quests',
      icon: TrophyIcon,
      path: '/quests',
      internal: true,
      highlight: true
    });
  }

  // Add NFT Collection as a menu item
  if (!menuItems.some(item => item.path === '/nft-collection')) {
    menuItems.push({
      name: 'NFT Collection',
      icon: PhotoIcon,
      path: '/nft-collection',
      internal: true,
      highlight: true
    });
  }

  // Add Settings link if user is logged in
  if (user?.isConnected) {
    menuItems.push({
      name: 'Settings',
      icon: Cog6ToothIcon,
      path: '/settings',
      internal: true
    });
  }


  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-40 md:hidden" onClick={toggleSidebar} />
      )}
      <div className={`w-80 h-screen bg-gradient-to-b from-dark-200 to-dark-100 fixed left-0 top-0 p-6 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 z-50 border-r border-violet-500/10 shadow-xl`}>
        {/* Background glow effects */}
        <div className="absolute -right-10 top-20 w-20 h-40 bg-violet-600/10 rounded-full filter blur-xl"></div>
        <div className="absolute -left-5 bottom-40 w-20 h-20 bg-purple-600/10 rounded-full filter blur-xl"></div>

        <div className="mb-10 relative">
          <div className="absolute -top-6 -left-6 w-20 h-20 bg-violet-600/10 rounded-full filter blur-xl"></div>
          <img src="/logo.png" alt="BitSport Logo" className="h-10 relative z-10" />
        </div>

        <nav className="relative z-10">
          <ul className="space-y-3">
            {menuItems.map((item) => {
              const active = isActive(item.path);

              return (
                <li key={item.name}>
                  {item.internal ? (
                    <Link
                      to={item.path}
                      className={`
                        relative group flex items-center space-x-3 p-3 rounded-xl
                        transition-all duration-300
                        ${active ? 'bg-gradient-to-r from-violet-600/20 to-purple-600/20 text-white font-semibold shadow-md' :
                          'text-gray-300 hover:bg-gradient-to-r hover:from-violet-600/10 hover:to-purple-600/10'}
                        ${item.adminOnly ? 'border border-purple-500/30' : ''}
                        ${item.highlight && !active ? 'text-violet-400 font-semibold' : ''}
                      `}
                    >
                      {/* Glow effect for active items */}
                      {active && (
                        <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-violet-600/20 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></span>
                      )}

                      <item.icon className={`h-5 w-5 ${active ? 'text-violet-400' : 'group-hover:text-violet-400'}`} />
                      <span>{item.name}</span>

                      {/* Active indicator */}
                      {active && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-violet-500 to-purple-600 rounded-r"></span>
                      )}
                    </Link>
                  ) : (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center space-x-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-violet-600/10 hover:to-purple-600/10 text-gray-300 transition-all duration-300"
                    >
                      <item.icon className="h-5 w-5 group-hover:text-violet-400" />
                      <span>{item.name}</span>
                    </a>
                  )}
                </li>
              );
            })}
          </ul>

          {user?.isConnected && (
            <div className="mt-8 backdrop-blur-sm bg-violet-500/5 rounded-xl p-3 border border-violet-500/20">
              <SidebarWallet />
            </div>
          )}

          <div className="mt-6 w-full">
            <LoginOptions />
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
